<template>
  <div
    class="altcraft__container"
    :class="[`altcraft__container--${type}`]">
    <img
      class="altcraft__illustration"
      :src="`/images/altcraft_popup/${type}.svg`"
      alt="">

    <section class="altcraft__section">
      <div class="altcraft-section__header">
        <h2 class="altcraft-section__h2">
          {{ text[type].label }}
        </h2>

        <button
          class="altcraft-section__close-btn"
          @click="closeLogic('denied')">
          <span class="shape-cross" />
        </button>
      </div>

      <p
        v-if="text[type].text"
        class="altcraft-section__p">
        {{ text[type].text }}
      </p>

      <component
        :is="type"
        class="altcraft__actions"
        @close="closeLogic"
        @subEmail="subEmail"
        @subPush="subPush" />
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import altcraftAnalytics from 'Root/drivers/altcraftAnalytics';
import AltcraftPush from 'Root/drivers/altcraftPush';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import altcraftScriptLoader from 'Root/mixins/altcraftScriptLoader';
import localStorageManager from 'Root/helpers/localStorageManager';

import AltcraftPopupModel from 'Models/AltcraftPopupModel';

import email from './AltcraftPopupEmail.vue';
import push from './AltcraftPopupPush.vue';
import pushPrompt from './AltcraftPopupPushPrompt.vue';

export default {
  name: 'AltcraftPopup',

  components: {
    email,
    push,
    pushPrompt,
  },

  mixins: [
    altcraftScriptLoader,
  ],

  props: {
    type: {
      type: String,
      required: true,
      validate: (type) => [
        AltcraftPopupModel.TYPE.EMAIL,
        AltcraftPopupModel.TYPE.PUSH,
        AltcraftPopupModel.TYPE.PUSH_PROMPT,
      ].includes(type),
    },
  },

  data() {
    return {
      text: AltcraftPopupModel.TEXT,
    };
  },

  computed: {
    ...mapState(['user']),
  },

  mounted() {
    if ('localStorage' in window) {
      if (this.type === AltcraftPopupModel.TYPE.EMAIL) {
        localStorageManager.setCooldown(AltcraftPopupModel.LOCALSTORAGE.EMAIL_COOLDOWN);
      }
    }

    googleTagAnalytics.emitAltcraftPopup(AltcraftPopupModel.ACTION.SHOWN, this.text[this.type].analyticsLabel);
  },

  methods: {
    closePopup() {
      this.$emit('close');
    },

    closeLogic(reason) {
      if (reason === 'denied') {
        googleTagAnalytics.emitAltcraftPopup(AltcraftPopupModel.ACTION.CLICKED_NO, this.text[this.type].analyticsLabel);

        if (this.type !== AltcraftPopupModel.TYPE.PUSH_PROMPT) {
          localStorageManager.setCooldown(this.text[this.type].cooldown);
        }
      }
      this.closePopup();
    },

    subEmail(mail) {
      altcraftAnalytics.subscribeToEmailChannel(mail, process.env.ALTCRAFT_RESOURCE_POPUP_ID);
      googleTagAnalytics.emitAltcraftPopup(AltcraftPopupModel.ACTION.CLICKED_YES, AltcraftPopupModel.LABEL.EMAIL_SUB);

      localStorageManager.setFlag(AltcraftPopupModel.LOCALSTORAGE.EMAIL_SUBSCRIBED, true);

      this.closePopup();
    },

    async subPush() {
      this.$emit('subPush');

      await this.loadAltcraftPush();

      const pushService = new AltcraftPush(new window.AKPush());
      await pushService.subscribe(AltcraftPopupModel.TARGET_FIELD.DEFAULT_AUTH, this.user.email).catch(() => 0);

      googleTagAnalytics.emitAltcraftPopup(
        AltcraftPopupModel.ACTION.CLICKED_YES,
        AltcraftPopupModel.LABEL.WEB_PUSH_SUB,
      );

      localStorageManager.setFlag(AltcraftPopupModel.LOCALSTORAGE.PUSH_SUBSCRIBED, true);

      this.closePopup();
    },
  },
};
</script>

<style lang="scss" scoped>
  .altcraft__container {
    position: fixed;
    max-width: 610px;
    background: linear-gradient(270deg, rgba($color--basic-blue, 0.9) 0%, rgba($color--basic-purple, 0.9) 100%);
    backdrop-filter: blur(4px);
    border-radius: 8px;
    display: flex;
    align-items: center;
    left: var(--padding);
    right: var(--padding);
    bottom: 20px;
    width: unset;
    padding: 16px;
    z-index: #{$z-index-intercom + 1};
  }

  .altcraft__illustration {
    display: none;
    height: 110px;
    width: 170px;
    object-fit: contain;
    margin-right: 24px;
  }

  .altcraft__section {
    display: grid;
    width: 100%;
    gap: 8px;
  }

  .altcraft-section__header {
    display: flex;
    justify-content: space-between;
  }

  .altcraft-section__h2 {
    font-size: $text-19px;
    line-height: 1;
    font-weight: 500;
    word-wrap: break-word;
  }

  .altcraft-section__p {
    font-size: $text-11px;
    word-break: break-word;
    font-weight: 300;
    line-height: 13px;
  }

  .altcraft-section__close-btn {
    background: none;
    border: none;
    font-size: 20px;
    align-self: flex-start;
    padding: 0;
    margin: 0 0 0 10px;
  }

  .shape-cross {
    display: block;
    width: 15px;
    height: 15px;
  }

  .altcraft__actions {
    margin-top: auto;
    height: 45px;
    width: 100%;
    padding-top: 5px;
    display: flex;
  }

  @media screen and #{$screen-more-or-640} {
    .altcraft__container {
      padding: 24px;
      left: 30px;
      bottom: 40px;
      z-index: 1000;
    }

    .altcraft__container--pushPrompt {
      top: 120px;
      left: 290px;
      bottom: unset !important;
      right: unset;
      max-width: 420px;
    }

    .altcraft__illustration {
      display: block;
    }

    .section__p {
      font-size: $text-13px;
      line-height: 1.4;
    }
  }
</style>
